<template>
  <section class="section">
    <div class="container">
      <Header v-bind="$route.meta" />
      <b-loading v-model="isLoading" :is-full-page="false" />

      <div class="level">
        <div class="level-left">
          <h1 class="title is-4">
            {{ $t('pages.client_crate_types.title') }}
          </h1>
        </div>
        <div class="level-right">
          <b-button type="is-primary" icon-left="plus" @click="openAdd">
            {{ $t('pages.client_crate_types.addType') }}
          </b-button>
        </div>
      </div>

      <p class="has-text-grey mb-4">
        {{ $t('pages.client_crate_types.intro') }}
      </p>

      <b-table :data="types" :paginated="types.length > 20" :per-page="20" hoverable>
        <b-table-column v-slot="props" field="name" :label="$t('pages.client_crate_types.name')" sortable>
          <strong>{{ props.row.name }}</strong>
        </b-table-column>
        <b-table-column v-slot="props" field="rootType" :label="$t('pages.client_crate_types.rootType')" sortable>
          {{ props.row.rootType }}
          <b-tag v-if="props.row.doubleWash" type="is-link is-light" class="ml-1">
            {{ $t('pages.client_crate_types.doubleWashTag') }}
          </b-tag>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.client_crate_types.plants')">
          <b-tag v-for="plant in props.row.plants" :key="plant" class="mr-1">
            {{ plantLabel(plant) }}
          </b-tag>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.client_crate_types.providers')">
          <b-tag v-for="code in partnerCodes(props.row, 'provider')" :key="code" type="is-info is-light" class="mr-1">
            {{ code }}
          </b-tag>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.client_crate_types.destinations')">
          <b-tag v-for="code in partnerCodes(props.row, 'destination')" :key="code" type="is-success is-light" class="mr-1">
            {{ code }}
          </b-tag>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.client_crate_types.actions')" centered width="160">
          <b-button size="is-small" icon-left="pen" class="mr-1" @click="openEdit(props.row)">
            {{ $t('global.update') }}
          </b-button>
          <b-button size="is-small" type="is-danger" icon-left="trash" @click="confirmArchive(props.row)" />
        </b-table-column>

        <template #empty>
          <div class="has-text-centered has-text-grey p-4">
            {{ $t('pages.client_crate_types.empty') }}
          </div>
        </template>
      </b-table>
    </div>

    <b-modal v-model="modalOpen" :width="640" scroll="keep">
      <div class="card">
        <header class="card-header">
          <p class="card-header-title">
            {{ isEdit ? $t('pages.client_crate_types.editType') : $t('pages.client_crate_types.addType') }}
          </p>
        </header>
        <div class="card-content">
          <b-field
            :label="$t('pages.client_crate_types.name')"
            :message="$t('pages.client_crate_types.nameHint')"
            :type="nameError ? 'is-danger' : ''"
          >
            <b-input v-model="form.name" placeholder="TSRC UBE 55" />
          </b-field>
          <p v-if="nameError" class="help is-danger mb-3">
            {{ nameError }}
          </p>

          <b-field
            :label="$t('pages.client_crate_types.rootType')"
            :message="$t('pages.client_crate_types.rootTypeHint')"
          >
            <b-select v-model="form.rootType" expanded>
              <option v-for="type in rootTypes" :key="type.value" :value="type.value">
                {{ type.name }}
              </option>
            </b-select>
          </b-field>

          <b-field :message="$t('pages.client_crate_types.doubleWashHint')">
            <b-switch v-model="form.doubleWash">
              {{ $t('pages.client_crate_types.doubleWash') }}
            </b-switch>
          </b-field>

          <b-field
            :label="$t('pages.client_crate_types.plants')"
            :message="$t('pages.client_crate_types.plantsHint')"
          >
            <b-taginput
              v-model="form.plants"
              :data="filteredPlants"
              field="name"
              autocomplete
              open-on-focus
              :placeholder="$t('pages.client_crate_types.plantsPlaceholder')"
              @typing="filterPlants"
            >
              <template slot-scope="props">
                {{ props.option.id }} — {{ props.option.name }}
              </template>
              <template #tag="props">
                {{ props.tag.id }}
              </template>
            </b-taginput>
          </b-field>

          <b-field
            :label="$t('pages.client_crate_types.providers')"
            :message="$t('pages.client_crate_types.providersHint')"
          >
            <b-taginput v-model="form.providers" :placeholder="$t('pages.client_crate_types.partnerPlaceholder')" />
          </b-field>

          <b-field
            :label="$t('pages.client_crate_types.destinations')"
            :message="$t('pages.client_crate_types.destinationsHint')"
          >
            <b-taginput v-model="form.destinations" :placeholder="$t('pages.client_crate_types.partnerPlaceholder')" />
          </b-field>

          <b-message v-if="pooledPlantWarning" type="is-warning" size="is-small" has-icon>
            {{ pooledPlantWarning }}
          </b-message>
        </div>
        <footer class="card-footer p-3">
          <b-button class="mr-2" @click="modalOpen = false">
            {{ $t('global.cancel') }}
          </b-button>
          <b-button type="is-primary" :loading="isSaving" @click="submit">
            {{ $t('global.save') }}
          </b-button>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
// Client-specific crate types: crates that went through a stricter preparation
// process for a demanding client. They stay ordinary crates -- a "TSRC UBE 55" IS a
// gps55 -- so SAP ships them under the root material and only the partner pair says
// which stock they belong to. This page is where that pairing is configured.
import ClientCrateType from '@/services/v3/clientCrateType'
import TargetStock from '@/services/v3/targetStock'
import plantSelector from '@/mixins/plantSelector'
import { rootTypeOptions } from '@/helpers/crateTypes'

// Mirrors the API's validateClientTypeName and the DB check constraint. The name is
// a key inside `type__STATUS`, so '_' and '%' (LIKE wildcards) and a leading 'GPS'
// (indistinguishable from a raw SAP material) are all fatal.
const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 .-]*$/

const emptyForm = () => ({ id: null, name: '', rootType: '5.5', doubleWash: false, plants: [], providers: [], destinations: [] })

export default {
  name: 'ClientCrateTypes',
  mixins: [plantSelector],
  data () {
    return {
      isLoading: false,
      isSaving: false,
      types: [],
      modalOpen: false,
      isEdit: false,
      filteredPlants: [],
      pooledPlants: [],
      form: emptyForm()
    }
  },
  computed: {
    rootTypes () {
      return rootTypeOptions()
    },
    nameError () {
      const name = (this.form.name || '').trim()
      if (name === '') return null
      if (!NAME_PATTERN.test(name)) return this.$t('pages.client_crate_types.invalidName')
      if (/^GPS/.test(name)) return this.$t('pages.client_crate_types.gpsName')
      return null
    },
    // A plant that pools 5.2 and 5.5 as 5.X still records movements against the real
    // sub-type, so the root has to be that sub-type and not the pool.
    pooledPlantWarning () {
      const selected = this.form.plants.map(p => p.id)
      const pooled = this.pooledPlants.filter(id => selected.includes(id))
      if (pooled.length === 0) return null
      return this.$t('pages.client_crate_types.pooledWarning', { plants: pooled.join(', '), root: this.form.rootType })
    }
  },
  async mounted () {
    await this.loadPlants({ force: true })
    this.filteredPlants = this.plants
    await this.loadData()
  },
  methods: {
    async loadData () {
      this.isLoading = true
      try {
        this.types = await ClientCrateType.getAll()
        await this.loadPooledPlants()
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 4000 })
      } finally {
        this.isLoading = false
      }
    },
    // Only the plants already carrying a type are checked: this is a hint on the
    // form, not a reason to read every site's config on load.
    async loadPooledPlants () {
      const plantIds = [...new Set(this.types.flatMap(t => t.plants))]
      const results = await Promise.all(plantIds.map(async (id) => {
        try {
          const target = await TargetStock.get({ id_site: id })
          if (!target || target.length === 0 || !target[0].types) return null
          return JSON.parse(target[0].types).some(x => x.name === '5.X') ? id : null
        } catch (e) {
          return null
        }
      }))
      this.pooledPlants = results.filter(Boolean)
    },
    plantLabel (id) {
      const name = this.plantNamesById[id]
      return name ? `${id} — ${name}` : id
    },
    partnerCodes (row, role) {
      return (row.partners || []).filter(p => p.role === role).map(p => p.partnerCode)
    },
    filterPlants (text) {
      const query = (text || '').toLowerCase()
      const chosen = this.form.plants.map(p => p.id)
      this.filteredPlants = this.plants.filter(p =>
        !chosen.includes(p.id) &&
        (String(p.id).toLowerCase().includes(query) || String(p.name || '').toLowerCase().includes(query))
      )
    },
    openAdd () {
      this.isEdit = false
      this.form = emptyForm()
      this.filteredPlants = this.plants
      this.modalOpen = true
    },
    openEdit (row) {
      this.isEdit = true
      this.form = {
        id: row.id,
        name: row.name,
        rootType: row.rootType,
        doubleWash: !!row.doubleWash,
        // Resolve against the full plant list so a hidden plant still shows its name.
        plants: row.plants.map(id => this.allPlants.find(p => p.id === id) || { id, name: id }),
        providers: this.partnerCodes(row, 'provider'),
        destinations: this.partnerCodes(row, 'destination')
      }
      this.filteredPlants = this.plants
      this.modalOpen = true
    },
    confirmArchive (row) {
      this.$buefy.dialog.confirm({
        title: this.$t('pages.client_crate_types.archiveTitle'),
        message: this.$t('pages.client_crate_types.archiveMessage', { name: row.name }),
        confirmText: this.$t('global.confirm'),
        cancelText: this.$t('global.cancel'),
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await ClientCrateType.archive(row.id)
            this.$buefy.toast.open({ type: 'is-primary', message: this.$t('pages.client_crate_types.archived'), duration: 3000 })
            await this.loadData()
          } catch (error) {
            this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 4000 })
          }
        }
      })
    },
    async submit () {
      const name = (this.form.name || '').trim()
      if (name === '' || this.nameError) {
        this.$buefy.toast.open({ type: 'is-danger', message: this.nameError || this.$t('pages.client_crate_types.requiredFields'), duration: 4000 })
        return
      }
      if (this.form.plants.length === 0) {
        this.$buefy.toast.open({ type: 'is-danger', message: this.$t('pages.client_crate_types.plantsRequired'), duration: 4000 })
        return
      }
      const partners = [
        ...this.form.providers.map(partnerCode => ({ partnerCode: String(partnerCode).trim(), role: 'provider' })),
        ...this.form.destinations.map(partnerCode => ({ partnerCode: String(partnerCode).trim(), role: 'destination' }))
      ].filter(p => p.partnerCode !== '')
      if (partners.length === 0) {
        this.$buefy.toast.open({ type: 'is-danger', message: this.$t('pages.client_crate_types.partnersRequired'), duration: 5000 })
        return
      }

      const payload = {
        id: this.form.id,
        name,
        rootType: this.form.rootType,
        doubleWash: this.form.doubleWash,
        plants: this.form.plants.map(p => p.id),
        partners
      }

      this.isSaving = true
      try {
        if (this.isEdit) {
          await ClientCrateType.update(payload)
        } else {
          await ClientCrateType.create(payload)
        }
        this.modalOpen = false
        this.$buefy.toast.open({ type: 'is-primary', message: this.$t('pages.client_crate_types.saved'), duration: 3000 })
        await this.loadData()
      } catch (error) {
        // The API rejects an overlap (two types claiming the same partner, root and
        // plant) with a message naming the clash — show it as-is.
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 6000 })
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>
